import React, { useReducer, useCallback, useEffect } from "react";
import TabContentSection from "./TabContentSection";
import { SuccessButton, EmptyContent } from "../../components";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import { IImageMemory } from "../../interfaces";
import { AddImageMemoryModal } from "../modals";
import { Spin, notification, Card, Col, Row } from "antd";
import { Backend } from "../../helpers";
import axios, { AxiosResponse, AxiosError, CancelTokenSource } from "axios";
import { useMediaQuery } from "react-responsive";

export type ImagesListState = {
  images: IImageMemory[];
  loading: boolean;
  currentImage?: IImageMemory;
  loadImagesTS: CancelTokenSource;
  upsertImageModalVisible: boolean;
};

export type ImagesListReduceAction = {
  type:
    | "updateState"
    | "showUpsertImageModal"
    | "closeUpsertImageModal"
    | "startLoading"
    | "stopLoading";
  state?: Partial<ImagesListState>;
};

const ImagesList: React.FC = (props) => {
  /**
   * State management
   */
  const reducer = (
    state: ImagesListState,
    action: ImagesListReduceAction
  ): ImagesListState => {
    switch (action.type) {
      case "showUpsertImageModal": {
        const { upsertImageModalVisible, ...rest } = state;
        return {
          upsertImageModalVisible: true,
          ...rest,
        };
      }
      case "closeUpsertImageModal": {
        const { upsertImageModalVisible, ...rest } = state;
        return {
          upsertImageModalVisible: false,
          ...rest,
        };
      }
      case "startLoading": {
        const { loading, ...rest } = state;
        return {
          loading: true,
          ...rest,
        };
      }
      case "stopLoading": {
        const { loading, ...rest } = state;
        return {
          loading: false,
          ...rest,
        };
      }
      case "updateState": {
        return {
          upsertImageModalVisible:
            action.state?.upsertImageModalVisible ??
            state.upsertImageModalVisible,
          currentImage: action.state?.currentImage ?? state.currentImage,
          loading: action.state?.loading ?? state.loading,
          images: action.state?.images ?? state.images,
          loadImagesTS: action.state?.loadImagesTS ?? state.loadImagesTS,
        };
      }
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    upsertImageModalVisible: false,
    loading: false,
    images: [],
    loadImagesTS: axios.CancelToken.source(),
  });

  const loadImages = useCallback(async () => {
    dispatch({ type: "startLoading" });
    await Backend.getInstance()
      .axios()
      .get("/memories/images/", {
        cancelToken: state.loadImagesTS.token,
      })
      .then((response: AxiosResponse<IImageMemory[]>) => {
        dispatch({ type: "updateState", state: { images: response.data } });
      })
      .catch((error: AxiosError<any>) => {
        notification.error({
          message: "Failed to load images. Try to refresh this page.",
        });
      })
      .finally(() => {
        dispatch({ type: "stopLoading" });
      });
  }, [state.loadImagesTS.token]);

  /**
   * Cancel pending request when the component is dismissed
   */
  useEffect(() => {
    loadImages();
    return () => {
      state.loadImagesTS.cancel();
    };
  }, [state.loadImagesTS, loadImages]);

  const isSm = useMediaQuery({ query: "(max-width: 640px)" });

  return (
    <Spin size="large" spinning={state.loading}>
      <TabContentSection
        title="My Images"
        button={
          <SuccessButton
            icon={<PlusOutlined />}
            className="rounded-md flex items-center"
            onClick={() => {
              dispatch({ type: "showUpsertImageModal" });
            }}
          >
            Add
          </SuccessButton>
        }
      >
        <AddImageMemoryModal
          visible={state.upsertImageModalVisible}
          initialValue={state.currentImage}
          handleCancel={(e) => {
            dispatch({ type: "closeUpsertImageModal" });
          }}
          onSuccess={(image: IImageMemory) => {}}
        />
        {state.images.length === 0 ? (
          <EmptyContent />
        ) : (
          <Row gutter={[16, 16]}>
            {state.images.map((item) => {
              return (
                <Col key={item.id} span={isSm ? 24 : 6}>
                  <Card
                    // style={{ width: 300 }}
                    className="w-full"
                    cover={
                      <img
                        alt="example"
                        src={`${Backend.getInstance().BASE_URL}/${item.image}`}
                      />
                    }
                    actions={[<EditOutlined key="edit" />]}
                  ></Card>
                </Col>
              );
            })}
          </Row>
        )}
      </TabContentSection>
    </Spin>
  );
};

export default ImagesList;
